import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/customer/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Trash2, Phone, AlertTriangle, Truck, ShoppingBag, Clock } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api, CHF, PHONE_DISPLAY, PHONE_TEL } from "@/lib/apiClient";
import { fetchTimeslots } from "@/lib/slots";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, updateQty, removeItem, itemUnitPrice, cartCount, delivery, setDelivery, clearCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    const base = { name: "", email: "", phone: "", address: "", postal_code: "", note: "" };
    try { return { ...base, ...JSON.parse(localStorage.getItem("ata_customer") || "{}"), note: "" }; }
    catch { return base; }
  });
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const mode = delivery?.mode || "pickup";
  const [slotData, setSlotData] = useState({ available: true, options: [], message: null });
  const slots = slotData.options;
  const timeslot = delivery?.timeslot || slots[0] || "";
  const changeSlot = (s) => setDelivery({ mode, postal_code: delivery?.postal_code || "", timeslot: s });

  useEffect(() => { fetchTimeslots().then(setSlotData); }, []);

  useEffect(() => {
    if (delivery?.postal_code) setForm((f) => ({ ...f, postal_code: delivery.postal_code }));
  }, [delivery]);

  // Mémorise les coordonnées client pour ne pas les ressaisir (hors note, spécifique à chaque commande).
  useEffect(() => {
    const { name, email, phone, address, postal_code } = form;
    localStorage.setItem("ata_customer", JSON.stringify({ name, email, phone, address, postal_code }));
  }, [form.name, form.email, form.phone, form.address, form.postal_code]);

  // Si le créneau enregistré n'est plus proposé (passé / hors horaires), on repositionne sur le premier disponible.
  useEffect(() => {
    if (slots.length && delivery && !slots.includes(delivery.timeslot)) {
      setDelivery({ ...delivery, timeslot: slots[0] });
    }
  }, [delivery, slots, setDelivery]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildPayload = useCallback((extra = {}) => ({
    mode, timeslot: delivery?.timeslot || "",
    items: cart.map((i) => ({ product_id: i.product_id, name: i.name, qty: i.qty, base_price: i.base_price, selected_addons: i.selected_addons, comment: i.comment })),
    customer: { ...form, email: form.email.toLowerCase() },
    promo_code: promo, ...extra,
  }), [mode, delivery, cart, form, promo]);

  const refreshQuote = useCallback(async (code = promo) => {
    if (cart.length === 0) return;
    try {
      const { data } = await api.post("/orders/quote", buildPayload({ promo_code: code }));
      setQuote(data);
    } catch (e) {
      setQuote(null);
    }
  }, [cart, buildPayload, promo]);

  useEffect(() => { refreshQuote(); /* eslint-disable-next-line */ }, [cart, form.postal_code]);

  const applyPromo = async () => {
    try {
      const { data } = await api.post("/orders/quote", buildPayload({ promo_code: promo }));
      setQuote(data);
      if (data.promo_code) toast.success("Code promo appliqué");
      else toast.error("Code promo invalide");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    }
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Nom, email et téléphone requis");
    if (mode === "delivery" && (!form.address || !form.postal_code)) return toast.error("Adresse et code postal requis pour la livraison");
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", buildPayload());
      const email = form.email.toLowerCase().trim();
      localStorage.setItem("ata_last_email", email);
      const mine = JSON.parse(localStorage.getItem("ata_my_orders") || "[]");
      localStorage.setItem("ata_my_orders", JSON.stringify([data.id, ...mine.filter((x) => x !== data.id)].slice(0, 15)));
      clearCart();
      toast.success(`Commande #${data.order_number} envoyée !`);
      navigate(`/mes-commandes?order=${data.order_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  if (cartCount === 0) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="empty-cart">
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Votre panier est vide</h1>
          <Button onClick={() => navigate("/commander")} className="mt-6 rounded-none bg-brand-red">Voir le menu</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8 grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3 space-y-6">
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">Finaliser la commande</h1>

          <div className="space-y-3" data-testid="checkout-fulfilment">
            <div className="flex items-center gap-2 text-sm font-medium">
              {mode === "delivery" ? <Truck className="w-4 h-4 text-brand-red" /> : <ShoppingBag className="w-4 h-4 text-brand-red" />}
              {mode === "delivery" ? "Livraison" : "À emporter"}{delivery?.postal_code ? ` · ${delivery.postal_code}` : ""}
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Créneau horaire</Label>
              {slots.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="checkout-slots">
                  {slots.map((s) => (
                    <button
                      key={s} type="button" data-testid={`checkout-slot-${s}`} onClick={() => changeSlot(s)}
                      className={`text-sm py-2 px-3 border transition-colors ${timeslot === s ? "bg-black text-white border-black" : "border-black/30 hover:border-black"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-brand-red font-medium mt-2" data-testid="checkout-slot-closed">{slotData.message || "Aucun créneau disponible pour le moment."}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Nom complet *</Label><Input value={form.name} onChange={set("name")} className="rounded-none border-black" data-testid="checkout-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} className="rounded-none border-black" data-testid="checkout-email" /></div>
            <div><Label>Téléphone *</Label><Input value={form.phone} onChange={set("phone")} className="rounded-none border-black" data-testid="checkout-phone" /></div>
            {mode === "delivery" && <div><Label>Code postal *</Label><Input value={form.postal_code} onChange={set("postal_code")} className="rounded-none border-black" data-testid="checkout-postal" /></div>}
            {mode === "delivery" && <div className="sm:col-span-2"><Label>Adresse de livraison *</Label><Input value={form.address} onChange={set("address")} className="rounded-none border-black" data-testid="checkout-address" /></div>}
            <div className="sm:col-span-2"><Label>Note (optionnel)</Label><Textarea value={form.note} onChange={set("note")} className="rounded-none border-black resize-none" data-testid="checkout-note" /></div>
          </div>

          <div className="bg-brand-amber text-black p-4 border-2 border-black flex items-start gap-3" data-testid="checkout-alert-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm font-medium">Si vous n'avez pas de confirmation dans les 2 minutes, appelez-nous au <a href={`tel:${PHONE_TEL}`} className="underline font-bold">{PHONE_DISPLAY}</a>.</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="border-2 border-black p-5 sticky top-24 space-y-4" data-testid="order-summary">
            <h2 className="font-display text-xl font-bold tracking-tight">Votre panier</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto">
              {cart.map((i) => (
                <div key={i.uid} className="flex gap-3" data-testid={`cart-item-${i.uid}`}>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{i.name}</p>
                    {i.selected_addons?.map((a, idx) => <p key={idx} className="text-xs text-muted-foreground">+ {a.name}{a.price > 0 ? ` (${CHF(a.price)})` : ""}</p>)}
                    {i.comment && <p className="text-xs italic text-muted-foreground">“{i.comment}”</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center border border-black">
                        <button onClick={() => updateQty(i.uid, i.qty - 1)} className="w-7 h-7 flex items-center justify-center" data-testid={`dec-${i.uid}`}><Minus className="w-3 h-3" /></button>
                        <span className="w-7 text-center text-sm">{i.qty}</span>
                        <button onClick={() => updateQty(i.uid, i.qty + 1)} className="w-7 h-7 flex items-center justify-center" data-testid={`inc-${i.uid}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <button onClick={() => removeItem(i.uid)} className="text-brand-red" data-testid={`rm-${i.uid}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <span className="font-semibold text-sm">{CHF(itemUnitPrice(i) * i.qty)}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Code promo" className="rounded-none border-black" data-testid="promo-input" />
              <Button onClick={applyPromo} variant="outline" className="rounded-none border-black" data-testid="apply-promo-btn">OK</Button>
            </div>

            <div className="border-t border-black/20 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Sous-total</span><span data-testid="summary-subtotal">{CHF(quote?.subtotal ?? 0)}</span></div>
              {quote?.discount_labels?.map((l, i) => <div key={i} className="flex justify-between text-brand-red"><span>{l}</span><span>-</span></div>)}
              {quote?.discount > 0 && <div className="flex justify-between text-brand-red"><span>Réductions</span><span data-testid="summary-discount">- {CHF(quote.discount)}</span></div>}
              {mode === "delivery" && <div className="flex justify-between"><span>Frais de livraison</span><span data-testid="summary-delivery">{CHF(quote?.delivery_fee ?? 0)}</span></div>}
              <div className="flex justify-between font-display text-xl font-bold pt-2"><span>Total</span><span data-testid="summary-total">{CHF(quote?.total ?? 0)}</span></div>
              <p className="text-xs text-muted-foreground">TVA 2.5% incluse</p>
            </div>

            {quote?.warning && <p className="text-sm text-brand-red font-medium" data-testid="quote-warning">{quote.warning}</p>}

            <Button onClick={submit} disabled={submitting || !!quote?.warning || slots.length === 0} className="w-full rounded-none bg-brand-red hover:bg-brand-red/90 h-12" data-testid="place-order-btn">
              {submitting ? "Envoi…" : slots.length === 0 ? "Fermé pour le moment" : `Commander · ${CHF(quote?.total ?? 0)}`}
            </Button>
            <a href={`tel:${PHONE_TEL}`} className="flex items-center justify-center gap-2 text-sm text-brand-red font-medium" data-testid="checkout-call">
              <Phone className="w-4 h-4" /> Besoin d'aide ? {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
